import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { adminNavItems } from '@/apps/admin/nav.config';
import { AppShell } from '@/components/shell/AppShell';
import { GlobalHeader } from '@/components/shell/GlobalHeader';
import { SkipLink } from '@/components/shell/SkipLink';
import { getAdminUsername, logoutAdmin } from '@/lib/auth/admin-auth';
import { useAdminMe } from '@/hooks/useAdminMe';
import { useTimezone } from '@/hooks/useTimezone';
import type { BreadcrumbSegment } from '@/types/nav';

function buildAdminBreadcrumb(pathname: string): BreadcrumbSegment[] {
  const parts = pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
  const segments: BreadcrumbSegment[] = [{ label: 'Admin', path: '/admin/home' }];
  let current = '/admin';
  for (const part of parts) {
    current += `/${part}`;
    segments.push({
      label: part.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
      path: current,
    });
  }
  return segments;
}

export function AdminLayout() {
  useTimezone();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: adminMe } = useAdminMe();
  const displayName = adminMe?.username ?? getAdminUsername() ?? 'Admin';

  const breadcrumb = useMemo(
    () => buildAdminBreadcrumb(location.pathname),
    [location.pathname],
  );

  return (
    <>
      <SkipLink />
      <AppShell
        portal="admin"
        navItems={adminNavItems}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        header={
          <GlobalHeader
            portal="admin"
            title={breadcrumb[breadcrumb.length - 1]?.label}
            onMenuToggle={() => setMobileOpen(true)}
            user={{ id: adminMe?.admin_id ?? displayName, displayName, portal: 'admin' }}
            onLogout={() => {
              logoutAdmin();
              navigate('/public/login?portal=admin');
            }}
          />
        }
      >
        <Outlet context={{ breadcrumb }} />
      </AppShell>
    </>
  );
}
